import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Plus, Search, Star, Users, UserCheck, UserX, Clock, ChevronDown } from 'lucide-react';
import './RecruitmentDashboard.css';

const statusColors = {
    Applied: { bg: '#e0f2fe', color: '#0369a1' },
    Shortlisted: { bg: '#fef9c3', color: '#854d0e' },
    'Phone Screen': { bg: '#ede9fe', color: '#6d28d9' },
    Interview: { bg: '#dbeafe', color: '#1d4ed8' },
    Hired: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fee2e2', color: '#991b1b' },
};

const AddCandidate = () => {
    const [candidates, setCandidates] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [jobFilter, setJobFilter] = useState('All');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [viewCandidate, setViewCandidate] = useState(null);
    const [currentCandidateId, setCurrentCandidateId] = useState(null);

    const initialFormState = { firstName: '', lastName: '', email: '', phone: '', jobId: '', status: 'Applied', rating: 0, notes: '', resumeUrl: '' };
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchCandidates();
        fetchJobs();
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setActiveDropdown(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    const fetchCandidates = async () => {
        try {
            const response = await api.get('/recruitment/candidates');
            setCandidates(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching candidates:", error);
            setLoading(false);
        }
    };

    const fetchJobs = async () => {
        try {
            const res = await api.get('/recruitment/jobs');
            setJobs(res.data);
        } catch (err) { console.error("Failed to load jobs", err); }
    };

    const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleAddClick = () => { setIsEditing(false); setCurrentCandidateId(null); setFormData(initialFormState); setShowModal(true); };

    const handleEditClick = (candidate) => {
        setIsEditing(true);
        setCurrentCandidateId(candidate._id);
        setFormData({
            firstName: candidate.firstName, lastName: candidate.lastName, email: candidate.email,
            phone: candidate.phone, jobId: candidate.jobId ? candidate.jobId._id : '',
            status: candidate.status, rating: candidate.rating || 0,
            notes: candidate.notes || '', resumeUrl: candidate.resumeUrl || ''
        });
        setShowModal(true);
        setActiveDropdown(null);
    };

    const handleViewClick = (candidate) => { setViewCandidate(candidate); setActiveDropdown(null); };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) { await api.put(`/recruitment/candidates/${currentCandidateId}`, formData); }
            else { await api.post('/recruitment/candidates', formData); }
            setShowModal(false); setFormData(initialFormState); setIsEditing(false); setCurrentCandidateId(null);
            fetchCandidates();
        } catch (error) { console.error('Error saving candidate', error); alert('Failed to save candidate'); }
    };

    const handleStatusChange = async (id, newStatus) => {
        setCandidates(prev => prev.map(c => c._id === id ? { ...c, status: newStatus } : c));
        try { await api.patch(`/recruitment/candidates/${id}/status`, { status: newStatus }); }
        catch (err) { console.error(err); fetchCandidates(); }
    };

    const toggleDropdown = (id, e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === id ? null : id); };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this candidate?')) {
            try { await api.delete(`/recruitment/candidates/${id}`); fetchCandidates(); }
            catch (err) { alert('Failed to delete candidate'); }
        }
        setActiveDropdown(null);
    };

    const renderStars = (rating) => Array.from({ length: 5 }, (_, i) => (
        <Star key={i} size={12} fill={i < rating ? '#f59e0b' : 'none'} color={i < rating ? '#f59e0b' : '#d1d5db'} />
    ));

    const filteredCandidates = candidates.filter(c => {
        const name = `${c.firstName} ${c.lastName}`.toLowerCase();
        const matchSearch = name.includes(searchTerm.toLowerCase()) || (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) || (c.jobId?.title || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'All' || c.status === statusFilter;
        const matchJob = jobFilter === 'All' || (c.jobId?._id === jobFilter || c.jobId === jobFilter);
        return matchSearch && matchStatus && matchJob;
    });

    const stats = {
        total: candidates.length,
        hired: candidates.filter(c => c.status === 'Hired').length,
        rejected: candidates.filter(c => c.status === 'Rejected').length,
        active: candidates.filter(c => !['Hired', 'Rejected'].includes(c.status)).length,
    };

    if (loading) return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <div className="skeleton skeleton-title" style={{ width: 200, marginBottom: 8 }} />
                    <div className="skeleton skeleton-text" style={{ width: 235 }} />
                </div>
                <div className="skeleton skeleton-btn" style={{ width: 145 }} />
            </div>

            {/* 4 Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[1,2,3,4].map(i => (
                    <div key={i} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div className="skeleton skeleton-text" style={{ width: 100, marginBottom: 8 }} />
                            <div className="skeleton" style={{ width: 52, height: 30, borderRadius: 6 }} />
                        </div>
                        <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0 }} />
                    </div>
                ))}
            </div>

            {/* Filters bar */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', background: 'white', padding: '14px 18px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div className="skeleton" style={{ flex: 1, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 150, height: 36, borderRadius: 8 }} />
                <div className="skeleton" style={{ width: 110, height: 36, borderRadius: 8 }} />
            </div>

            {/* Table */}
            <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1.5px solid #e8edf2', background: '#f8fafc', gap: 0 }}>
                    {[{ w: 170 }, { w: 120 }, { w: 140 }, { w: 80 }, { w: 100 }, { w: 92 }, { w: 48 }].map((col, i) => (
                        <div key={i} className="skeleton" style={{ height: 11, borderRadius: 4, width: col.w, marginRight: i < 6 ? 14 : 0, flexShrink: 0 }} />
                    ))}
                </div>
                {/* Data rows */}
                {[1,2,3,4,5,6,7].map(i => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #f1f5f9', gap: 0 }}>
                        {/* Candidate: avatar + name + email */}
                        <div style={{ width: 170, flexShrink: 0, marginRight: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
                            <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
                            <div>
                                <div className="skeleton skeleton-text" style={{ width: 90, marginBottom: 5 }} />
                                <div className="skeleton skeleton-text" style={{ width: 105 }} />
                            </div>
                        </div>
                        {/* Job Applied */}
                        <div className="skeleton skeleton-text" style={{ width: 100, flexShrink: 0, marginRight: 14 }} />
                        {/* Contact: phone + email stacked */}
                        <div style={{ width: 140, flexShrink: 0, marginRight: 14 }}>
                            <div className="skeleton skeleton-text" style={{ width: '75%', marginBottom: 5 }} />
                            <div className="skeleton skeleton-text" style={{ width: '88%' }} />
                        </div>
                        {/* Rating: 5 stars */}
                        <div style={{ display: 'flex', gap: 3, width: 80, flexShrink: 0, marginRight: 14 }}>
                            {[1,2,3,4,5].map(s => <div key={s} className="skeleton" style={{ width: 12, height: 12, borderRadius: '50%' }} />)}
                        </div>
                        {/* Applied Date */}
                        <div className="skeleton skeleton-text" style={{ width: 80, flexShrink: 0, marginRight: 14 }} />
                        {/* Status badge with caret */}
                        <div className="skeleton" style={{ width: 82, height: 28, borderRadius: 20, flexShrink: 0, marginRight: 14 }} />
                        {/* Actions: 3-dot */}
                        <div className="skeleton" style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0 }} />
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="recruitment-container">
            {/* Header */}
            <div className="recruitment-header">
                <div>
                    <h2>Candidate Database</h2>
                    <div className="breadcrumb"><span className="bc-parent">Recruit</span><span className="bc-sep"> • </span><span className="bc-current">Candidate Database</span></div>
                </div>
                <button onClick={handleAddClick} className="rc-btn rc-btn-primary">
                    <Plus size={16} /> Add Candidate
                </button>
            </div>

            {/* Stats */}
            <div className="rc-stat-grid">
                {[
                    { label: 'Total Candidates', val: stats.total, color: '#6366f1', bg: '#eef2ff', icon: <Users size={20} /> },
                    { label: 'Active Pipeline', val: stats.active, color: '#f59e0b', bg: '#fffbeb', icon: <Clock size={20} /> },
                    { label: 'Hired', val: stats.hired, color: '#10b981', bg: '#ecfdf5', icon: <UserCheck size={20} /> },
                    { label: 'Rejected', val: stats.rejected, color: '#ef4444', bg: '#fff1f2', icon: <UserX size={20} /> },
                ].map((s, i) => (
                    <div key={i} className="rc-stat-card">
                        <div>
                            <div className="rc-stat-label">{s.label}</div>
                            <div className="rc-stat-value" style={{ color: s.color }}>{s.val}</div>
                        </div>
                        <div className="rc-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                    </div>
                ))}
            </div>

            {/* Filters Bar */}
            <div className="rc-filters">
                <div className="rc-search-wrap">
                    <Search size={15} />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by name, email, or job title..." />
                </div>
                <select className="rc-select" style={{ minWidth: 150 }} value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
                    <option value="All">All Jobs</option>
                    {jobs.map(j => <option key={j._id} value={j._id}>{j.title}</option>)}
                </select>
                <select className="rc-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="All">All Status</option>
                    {['Applied', 'Shortlisted', 'Phone Screen', 'Interview', 'Hired', 'Rejected'].map(s => <option key={s}>{s}</option>)}
                </select>
            </div>

            {/* Candidates Table */}
            <div className="dark-card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>Candidate</th>
                            <th>Contact</th>
                            <th>Applied For</th>
                            <th>Rating</th>
                            <th>Date Applied</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCandidates.length > 0 ? filteredCandidates.map(candidate => {
                            const sc = statusColors[candidate.status] || { bg: '#f1f5f9', color: '#475569' };
                            return (
                                <tr key={candidate._id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                                                {candidate.firstName?.[0]?.toUpperCase()}{candidate.lastName?.[0]?.toUpperCase()}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{candidate.firstName} {candidate.lastName}</div>
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{candidate.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ fontSize: 13, color: '#475569' }}>{candidate.phone || '—'}</td>
                                    <td style={{ color: '#475569', fontSize: 13 }}>{candidate.jobId?.title || 'General Application'}</td>
                                    <td><div style={{ display: 'flex', gap: 2 }}>{renderStars(candidate.rating || 0)}</div></td>
                                    <td style={{ fontSize: 13, color: '#475569' }}>{new Date(candidate.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <select value={candidate.status} onChange={e => handleStatusChange(candidate._id, e.target.value)}
                                                style={{ ...sc, padding: '4px 24px 4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                                                {['Applied', 'Shortlisted', 'Phone Screen', 'Interview', 'Hired', 'Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                            <ChevronDown size={12} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: sc.color }} />
                                        </div>
                                    </td>
                                    <td style={{ position: 'relative' }}>
                                        <button onClick={e => toggleDropdown(candidate._id, e)} className="rc-icon-btn">⋮</button>
                                        {activeDropdown === candidate._id && (
                                            <div style={{ position: 'absolute', right: 10, top: 40, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 150, overflow: 'hidden' }}>
                                                <div style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => handleViewClick(candidate)}>👤 View Profile</div>
                                                <div style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => handleEditClick(candidate)}>✏️ Edit</div>
                                                <div style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => handleDelete(candidate._id)}>🗑️ Delete</div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan="7" style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No candidates found. Click "Add Candidate" to add one.</td></tr>
                        )}
                    </tbody>
                </table>
                <div className="rc-table-footer">
                    Showing {filteredCandidates.length} of {candidates.length} candidates
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal wide">
                        <div className="rc-modal-header">
                            <h3 className="rc-modal-title">{isEditing ? 'Edit Candidate' : 'Add New Candidate'}</h3>
                            <button className="rc-modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleFormSubmit}>
                            <div className="form-group">
                                <label>Applying For (Job) *</label>
                                <select name="jobId" value={formData.jobId} onChange={handleFormChange}>
                                    <option value="">Select a Job Position</option>
                                    {jobs.map(job => <option key={job._id} value={job._id}>{job.title}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>First Name *</label>
                                    <input name="firstName" required value={formData.firstName} onChange={handleFormChange} placeholder="e.g. John" />
                                </div>
                                <div className="form-group">
                                    <label>Last Name *</label>
                                    <input name="lastName" required value={formData.lastName} onChange={handleFormChange} placeholder="e.g. Doe" />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input type="email" name="email" required value={formData.email} onChange={handleFormChange} placeholder="john@example.com" />
                                </div>
                                <div className="form-group">
                                    <label>Phone</label>
                                    <input name="phone" value={formData.phone} onChange={handleFormChange} placeholder="+1 234 567 890" />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select name="status" value={formData.status} onChange={handleFormChange}>
                                        {['Applied', 'Shortlisted', 'Phone Screen', 'Interview', 'Hired', 'Rejected'].map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Rating (1-5)</label>
                                    <select name="rating" value={formData.rating} onChange={handleFormChange}>
                                        <option value={0}>No rating</option>
                                        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} star{n !== 1 ? 's' : ''}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Resume URL</label>
                                <input name="resumeUrl" value={formData.resumeUrl} onChange={handleFormChange} placeholder="https://..." />
                            </div>
                            <div className="form-group">
                                <label>Notes</label>
                                <textarea name="notes" rows={3} value={formData.notes} onChange={handleFormChange} placeholder="Any notes about this candidate..." />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                                <button type="button" onClick={() => setShowModal(false)} className="rc-btn rc-btn-secondary">Cancel</button>
                                <button type="submit" className="rc-btn rc-btn-primary">{isEditing ? 'Update' : 'Add Candidate'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Candidate Modal */}
            {viewCandidate && (
                <div className="rc-modal-overlay">
                    <div className="rc-modal">
                        <div className="rc-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18 }}>
                                    {viewCandidate.firstName?.[0]}{viewCandidate.lastName?.[0]}
                                </div>
                                <div>
                                    <h3 className="rc-modal-title">{viewCandidate.firstName} {viewCandidate.lastName}</h3>
                                    <div style={{ fontSize: 12, color: 'var(--rc-text-2)', marginTop: 2 }}>{viewCandidate.email}</div>
                                </div>
                            </div>
                            <button className="rc-modal-close" onClick={() => setViewCandidate(null)}>×</button>
                        </div>
                        <div style={{ padding: 24 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                {[
                                    { l: 'Job', v: viewCandidate.jobId?.title || 'General Application' },
                                    { l: 'Phone', v: viewCandidate.phone || '—' },
                                    { l: 'Status', v: viewCandidate.status, isStatus: true },
                                    { l: 'Applied', v: new Date(viewCandidate.createdAt).toLocaleDateString() },
                                ].map((f, i) => (
                                    <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{f.l}</div>
                                        <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>
                                            {f.isStatus ? <span style={{ ...(statusColors[f.v] || {}), padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{f.v}</span> : f.v}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Rating</div>
                                <div style={{ display: 'flex', gap: 3 }}>{renderStars(viewCandidate.rating || 0)}</div>
                            </div>
                            {viewCandidate.notes && (
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{viewCandidate.notes}</div>
                                </div>
                            )}
                            {viewCandidate.resumeUrl && (
                                <div style={{ marginBottom: 16 }}>
                                    <a href={viewCandidate.resumeUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontSize: 13, fontWeight: 600 }}>View Resume →</a>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: '1px solid var(--rc-border-light)' }}>
                                <button onClick={() => setViewCandidate(null)} className="rc-btn rc-btn-secondary">Close</button>
                                <button onClick={() => { setViewCandidate(null); handleEditClick(viewCandidate); }} className="rc-btn rc-btn-primary">Edit Profile</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddCandidate;
